/**
 * Minimaler API-Client mit Bearer-Auth + Auto-Refresh bei 401.
 */

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export interface ApiError extends Error {
  status: number;
  detail: string;
}

class HttpError extends Error implements ApiError {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(`HTTP ${status}: ${detail}`);
    this.status = status;
    this.detail = detail;
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export async function attemptRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const resp = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'same-origin',
        });
        if (!resp.ok) return false;
        const data = (await resp.json()) as { access_token: string };
        accessToken = data.access_token;
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
  retried?: boolean;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { body, skipAuth, retried, headers, ...rest } = opts;
  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...((headers as Record<string, string>) ?? {}),
  };
  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (!skipAuth && accessToken) {
    finalHeaders.Authorization = `Bearer ${accessToken}`;
  }

  const resp = await fetch(path, {
    ...rest,
    headers: finalHeaders,
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 401 && !skipAuth && !retried) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      return api<T>(path, { ...opts, retried: true });
    }
    accessToken = null;
  }

  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const j = await resp.json();
      if (typeof j?.detail === 'string') detail = j.detail;
    } catch {
      // body might not be json
    }
    throw new HttpError(resp.status, detail);
  }

  if (resp.status === 204) return undefined as T;
  const contentType = resp.headers.get('Content-Type') ?? '';
  if (!contentType.includes('json')) return undefined as T;
  return (await resp.json()) as T;
}

export interface TokenPair {
  access_token: string;
  token_type: 'bearer';
  expires_in: number;
  must_reset_pw: boolean;
}

export interface MeResponse {
  id: string;
  email: string;
  full_name: string;
  role: 'super_admin' | 'operator_admin' | 'dispatcher' | 'employee';
  must_reset_pw: boolean;
  tenant_id: string | null;
  profile_image_url: string | null;
  last_login_at: string | null;
  branding: {
    tenant_slug: string | null;
    tenant_name: string | null;
    primary_color: string;
    logo_url: string | null;
  };
}
